import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Power, Clock, RefreshCw, Send, CheckCircle2, AlertCircle, Mail,
  Calendar
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];
const DAY_PRESETS = [3, 5, 10];

function fmtRelative(iso) {
  if (!iso) return "Niciodată";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `acum ${Math.floor(diff)}s`;
    if (diff < 3600) return `acum ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `acum ${Math.floor(diff / 3600)} h`;
    return d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

function fmtInterval(sec) {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  if (sec < 86400) return `${Math.round(sec / 3600)} h`;
  return `${Math.round(sec / 86400)} zile`;
}

function LoopCard({ loop, onPatch, onTestSend, onShowHistory, templates }) {
  const [iv, setIv] = useState(loop.config.interval_sec);
  const [saving, setSaving] = useState(false);
  const [frequency, setFrequency] = useState(loop.config.frequency || "weekly");
  const [weekday, setWeekday] = useState(loop.config.weekday ?? 0);
  const [dayOfMonth, setDayOfMonth] = useState(loop.config.day_of_month ?? 1);
  const [hour, setHour] = useState(loop.config.hour ?? 9);
  const [recipients, setRecipients] = useState((loop.config.recipients || []).join(", "));
  const [daysThreshold, setDaysThreshold] = useState(loop.config.days_threshold ?? 3);
  const [templateId, setTemplateId] = useState(loop.config.template_id || "");
  const [testing, setTesting] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const isScheduled = loop.kind === "scheduled";
  const isRmaAlert = loop.key === "rma_incident_alert";
  const isFbeReport = loop.key === "fbe_weekly_report";
  const isVidenda = loop.key === "videnda_monthly_report";
  const hasFrequency = isFbeReport || isVidenda;
  // Videnda is always monthly — fixed frequency, only day_of_month + hour editable
  const effectiveFreq = isVidenda ? "monthly" : frequency;
  const daysIsPreset = DAY_PRESETS.includes(Number(daysThreshold));

  useEffect(() => {
    setIv(loop.config.interval_sec);
    setFrequency(loop.config.frequency || "weekly");
    setWeekday(loop.config.weekday ?? 0);
    setDayOfMonth(loop.config.day_of_month ?? 1);
    setHour(loop.config.hour ?? 9);
    setRecipients((loop.config.recipients || []).join(", "));
    setDaysThreshold(loop.config.days_threshold ?? 3);
    setTemplateId(loop.config.template_id || "");
  }, [loop]);

  const lastRun = loop.last_run;
  const enabled = loop.config.enabled;

  const toggle = async () => {
    setSaving(true);
    try {
      await onPatch(loop.key, { enabled: !enabled });
      toast.success(`${!enabled ? "Activat" : "Dezactivat"}: ${loop.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare salvare");
    } finally { setSaving(false); }
  };

  const saveInterval = async () => {
    setSaving(true);
    try {
      const patch = { interval_sec: Number(iv) };
      if (isScheduled) {
        patch.hour = Number(hour);
        patch.recipients = recipients;
        if (isFbeReport) {
          patch.frequency = frequency;
          if (frequency === "weekly") patch.weekday = Number(weekday);
          if (frequency === "monthly") patch.day_of_month = Number(dayOfMonth);
        } else if (isVidenda) {
          patch.day_of_month = Number(dayOfMonth);
        } else {
          patch.weekday = Number(weekday);
        }
      }
      if (isRmaAlert) {
        patch.days_threshold = Number(daysThreshold) || 3;
        patch.template_id = templateId || null;
      }
      await onPatch(loop.key, patch);
      toast.success("Salvat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare salvare");
    } finally { setSaving(false); }
  };

  const downloadZip = async () => {
    try {
      const r = await axios.get(`${API}/settings/background-loops/videnda_monthly_report/download`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Fise_Videnda.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await onTestSend(loop.key);
      toast.success("Trimis cu succes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trimiterea a eșuat");
    } finally { setTesting(false); }
  };

  return (
    <div
      data-testid={`loop-card-${loop.key}`}
      className={`rounded-xl border p-4 transition ${
        enabled ? "border-zinc-800 bg-zinc-950" : "border-zinc-900 bg-zinc-950/40 opacity-70"
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Activity className={`h-4 w-4 ${enabled ? "text-emerald-400" : "text-zinc-600"}`} />
            <h3 className="text-sm font-semibold text-zinc-100">{loop.label}</h3>
            {isScheduled && (
              <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                Programat
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-zinc-500">{loop.description}</p>
        </div>
        <button
          data-testid={`loop-toggle-${loop.key}`}
          onClick={toggle}
          disabled={saving}
          className={`inline-flex h-8 w-14 flex-shrink-0 items-center rounded-full border px-1 transition ${
            enabled ? "border-emerald-500/40 bg-emerald-500/20" : "border-zinc-800 bg-zinc-900"
          } disabled:opacity-50`}
          title={enabled ? "Dezactivează" : "Activează"}
        >
          <span className={`h-6 w-6 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : ""}`} />
        </button>
      </div>

      <div className="mb-3 flex items-center gap-4 text-[11px] text-zinc-500">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {(() => {
            if (!isScheduled) return `La fiecare ${fmtInterval(loop.config.interval_sec)}`;
            const h = String(loop.config.hour ?? 9).padStart(2, "0");
            const f = loop.config.frequency;
            if (isVidenda) return `Lunar pe ${loop.config.day_of_month ?? 1} la ${h}:00 RO`;
            if (isFbeReport && f === "daily")   return `Zilnic la ${h}:00 RO`;
            if (isFbeReport && f === "monthly") return `Lunar pe ${loop.config.day_of_month ?? 1} la ${h}:00 RO`;
            if (isFbeReport && f === "custom")  return `La fiecare ${fmtInterval(loop.config.interval_sec)}`;
            if (loop.config.weekday === -1)     return `Zilnic la ${h}:00 RO`;
            return `${WEEKDAYS[loop.config.weekday ?? 0]} la ${h}:00 RO`;
          })()}
        </span>
        <span className="inline-flex items-center gap-1">
          {lastRun?.ok === false ? (
            <AlertCircle className="h-3 w-3 text-rose-400" />
          ) : lastRun ? (
            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          ) : (
            <span className="h-3 w-3 rounded-full bg-zinc-700" />
          )}
          {lastRun ? fmtRelative(lastRun.ran_at) : "Niciodată rulat"}
          {lastRun?.error && (
            <span className="ml-1 truncate text-rose-300/80" title={lastRun.error}>
              · err
            </span>
          )}
        </span>
      </div>

      {/* Interval input (for periodic) */}
      {!isScheduled && (
        <div className="mb-3 flex items-end gap-2">
          <div className="flex-1">
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
              Interval (secunde)
            </label>
            <input
              data-testid={`loop-interval-${loop.key}`}
              type="number"
              min={loop.min_interval_sec}
              max={loop.max_interval_sec}
              value={iv}
              onChange={(e) => setIv(e.target.value)}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
            />
          </div>
          <button
            data-testid={`loop-save-${loop.key}`}
            onClick={saveInterval}
            disabled={saving || Number(iv) === loop.config.interval_sec}
            className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-orange-500/40 hover:text-orange-200 disabled:opacity-40"
          >
            Salvează
          </button>
        </div>
      )}

      {/* Scheduled controls */}
      {isScheduled && (
        <div className="space-y-2">
          {isFbeReport && (
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                Frecvență
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { v: "daily",   l: "Zilnic" },
                  { v: "weekly",  l: "Săptămânal" },
                  { v: "monthly", l: "Lunar" },
                  { v: "custom",  l: "Custom" },
                ].map((f) => (
                  <button key={f.v} type="button"
                    data-testid={`loop-freq-${f.v}`}
                    onClick={() => setFrequency(f.v)}
                    className={`rounded-lg border px-2 py-1.5 text-xs font-semibold transition ${
                      frequency === f.v
                        ? "border-orange-500 bg-orange-500/15 text-orange-200"
                        : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-orange-500/40"
                    }`}>
                    {f.l}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className={`grid gap-2 ${(isRmaAlert || (isFbeReport && (frequency === "daily" || frequency === "custom"))) ? "grid-cols-1" : "grid-cols-2"}`}>
            {!isRmaAlert && !isVidenda && (isFbeReport ? frequency === "weekly" : true) && (
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                  <Calendar className="mr-1 inline h-3 w-3" /> Ziua
                </label>
                <select
                  data-testid={`loop-weekday-${loop.key}`}
                  value={weekday}
                  onChange={(e) => setWeekday(e.target.value)}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                >
                  {WEEKDAYS.map((w, i) => (
                    <option key={i} value={i}>{w}</option>
                  ))}
                </select>
              </div>
            )}
            {(isVidenda || (isFbeReport && frequency === "monthly")) && (
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                  <Calendar className="mr-1 inline h-3 w-3" /> Ziua din lună
                </label>
                <input type="number" min="1" max="31"
                  data-testid="loop-day-of-month"
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                />
                <p className="mt-1 text-[10px] text-zinc-500">Lunile cu mai puține zile sunt clamp-uite (31 → 28/30 după caz).</p>
              </div>
            )}
            {isFbeReport && frequency === "custom" ? (
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                  <Clock className="mr-1 inline h-3 w-3" /> Interval (secunde) — pentru custom
                </label>
                <input type="number" min={loop.min_interval_sec} max={loop.max_interval_sec}
                  data-testid="loop-interval-custom"
                  value={iv}
                  onChange={(e) => setIv(e.target.value)}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                />
                <p className="mt-1 text-[10px] text-zinc-500">
                  Min {loop.min_interval_sec}s · Max {fmtInterval(loop.max_interval_sec)}. Ex: 86400 = zilnic (24h).
                </p>
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                  <Clock className="mr-1 inline h-3 w-3" /> Ora (RO){isRmaAlert ? " · zilnic" : ""}
                </label>
                <select
                  data-testid={`loop-hour-${loop.key}`}
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                >
                {Array.from({ length: 24 }).map((_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
            </div>
            )}
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
              <Mail className="mr-1 inline h-3 w-3" /> Destinatari (separați prin virgulă)
            </label>
            <input
              data-testid={`loop-recipients-${loop.key}`}
              type="text"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="email1@firma.ro, email2@firma.ro"
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500/50 focus:outline-none"
            />
          </div>
          {isRmaAlert && (
            <>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                  Trimite cu cât timp înainte de &quot;finalizare cu incident&quot;
                </label>
                <div className="flex gap-1.5 items-center">
                  {DAY_PRESETS.map((d) => (
                    <button key={d} type="button"
                      data-testid={`loop-days-${d}`}
                      onClick={() => setDaysThreshold(d)}
                      className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-semibold transition ${
                        Number(daysThreshold) === d
                          ? "border-orange-500 bg-orange-500/15 text-orange-200"
                          : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-orange-500/40"
                      }`}>
                      {d} zile
                    </button>
                  ))}
                  <input type="number" min="1" max="30"
                    data-testid="loop-days-custom"
                    value={daysIsPreset ? "" : daysThreshold}
                    onChange={(e) => setDaysThreshold(e.target.value)}
                    placeholder="custom"
                    className={`w-20 rounded-lg border px-2 py-1.5 text-center text-xs ${
                      daysIsPreset
                        ? "border-zinc-800 bg-zinc-900 text-zinc-500"
                        : "border-orange-500 bg-orange-500/15 text-orange-200"
                    } placeholder:text-zinc-600 focus:border-orange-500/50 focus:outline-none`}
                  />
                </div>
                <p className="mt-1 text-[10px] text-zinc-500">
                  Ex.: dacă data limită este 20.06.2026 și alegi &quot;3 zile&quot;, primești email pe 17.06.2026 la ora setată mai sus.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
                  Șablon email folosit
                </label>
                <select
                  data-testid="loop-template"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
                >
                  <option value="">— Fără șablon (folosește mesaj implicit) —</option>
                  {(templates || []).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {!templateId && (
                  <p className="mt-1 text-[10px] text-amber-400">
                    Dacă nu alegi un șablon, se va trimite un email implicit (sumar tabel cu RMA-urile aproape de termen).
                  </p>
                )}
              </div>
            </>
          )}
          <div className="flex gap-2">
            <button
              data-testid={`loop-save-${loop.key}`}
              onClick={saveInterval}
              disabled={saving}
              className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-orange-500/40 hover:text-orange-200 disabled:opacity-40"
            >
              Salvează
            </button>
            {isVidenda && (
              <button
                data-testid="loop-download-videnda"
                onClick={downloadZip}
                className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-1.5 text-xs font-semibold text-sky-200 hover:bg-sky-500/20"
                title="Descarcă ZIP cu cele 3 fișe (luna anterioară)"
              >
                ZIP
              </button>
            )}
            {isVidenda && (
              <button
                data-testid="loop-update-videnda-templates"
                onClick={() => setTemplatesOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/20"
                title="Încarcă/actualizează șabloanele Ambalaje / Baterii / EEE"
              >
                Update Tabele
              </button>
            )}
            {isVidenda && (
              <button
                data-testid="loop-resend-month-videnda"
                onClick={() => setResendOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-200 hover:bg-violet-500/20"
                title="Retrimite raportul pentru o lună specifică"
              >
                Retrimite lună
              </button>
            )}
            <button
              data-testid={`loop-test-${loop.key}`}
              onClick={test}
              disabled={testing}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
            >
              <Send className="h-3 w-3" /> {testing ? "..." : "Trimite acum"}
            </button>
            <button
              data-testid={`loop-history-${loop.key}`}
              onClick={() => onShowHistory(loop.key)}
              className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-orange-500/40 hover:text-orange-200"
              title="Istoric trimiteri"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
      {templatesOpen && (
        <VidendaTemplatesModal onClose={() => setTemplatesOpen(false)} />
      )}
      {resendOpen && (
        <VidendaResendMonthModal onClose={() => setResendOpen(false)} />
      )}
    </div>
  );
}

function VidendaTemplatesModal({ onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagMonth, setDiagMonth] = useState(() => {
    const d = new Date();
    const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    return `${String(p.getMonth() + 1).padStart(2, "0")}.${p.getFullYear()}`;
  });
  const [diagBusy, setDiagBusy] = useState(false);

  const runDiag = async () => {
    setDiagBusy(true); setDiag(null);
    try {
      const r = await axios.get(`${API}/videnda/debug`, { params: { month: diagMonth } });
      setDiag(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setDiagBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/settings/background-loops/videnda_monthly_report/templates`);
      setTemplates(r.data.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/settings/background-loops/videnda_monthly_report/templates/${key}`, fd);
      toast.success(`Șablon ${key} actualizat`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setUploadingKey(null); }
  };

  const LABELS = { ambalaje: "Ambalaje", baterii: "Baterii", eee: "EEE" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        data-testid="videnda-templates-modal"
        className="w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 text-base font-semibold text-zinc-100">Update Tabele Videnda</h3>
        <p className="mb-4 text-xs text-zinc-500">
          Încarcă noile șabloane XLSX pentru Ambalaje, Baterii și EEE. Sistemul le folosește automat pentru toate raportările viitoare (inclusiv retrimiterea unei luni anterioare).
        </p>
        {loading && <p className="text-sm text-zinc-500">Încărcare...</p>}
        {!loading && (
          <div className="space-y-3">
            {templates.map((t) => (
              <div key={t.key} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-zinc-100">{LABELS[t.key] || t.key}</div>
                    <div className="text-[11px] text-zinc-500">
                      {t.in_db ? (
                        <>
                          <span className="text-emerald-300">✓ În DB</span>
                          {" · "}
                          {t.filename}
                          {" · "}
                          {Math.round((t.size || 0) / 1024)} KB
                          {t.updated_at && (
                            <>
                              {" · "}
                              actualizat {new Date(t.updated_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })}
                            </>
                          )}
                        </>
                      ) : (
                        <span className="text-amber-300">⚠ Doar fallback disk — încarcă un XLSX aici</span>
                      )}
                    </div>
                  </div>
                  <label
                    data-testid={`videnda-upload-${t.key}`}
                    className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/20 ${uploadingKey === t.key ? "opacity-60" : ""}`}
                  >
                    {uploadingKey === t.key ? "Se încarcă..." : "Încarcă XLSX"}
                    <input
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      className="hidden"
                      disabled={uploadingKey === t.key}
                      onChange={(e) => upload(t.key, e.target.files?.[0])}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 rounded-lg border border-sky-500/30 bg-sky-500/5 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-sky-200">Diagnostic Importuri (Ambalaje)</div>
              <p className="text-[11px] text-zinc-500">Vezi câte importuri are DB-ul pentru o lună — filtrate după <code>stock_date</code> (Data Stoc).</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid="videnda-diag-month"
                type="text" placeholder="MM.YYYY"
                value={diagMonth}
                onChange={(e) => setDiagMonth(e.target.value)}
                className="w-24 rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100"
              />
              <button
                data-testid="videnda-diag-run"
                onClick={runDiag} disabled={diagBusy}
                className="rounded border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-xs font-semibold text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
              >
                {diagBusy ? "..." : "Rulează"}
              </button>
            </div>
          </div>
          {diag && (
            <div className="space-y-1 text-[11px]">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded bg-zinc-900 px-2 py-1">
                  <div className="text-zinc-500">Total în DB</div>
                  <div className="font-mono font-bold text-zinc-200">{diag.imports_total_in_db}</div>
                </div>
                <div className="rounded bg-zinc-900 px-2 py-1">
                  <div className="text-zinc-500">Match stock_date</div>
                  <div className="font-mono font-bold text-emerald-300">{diag.imports_matching_stock_date_query}</div>
                </div>
                <div className="rounded bg-zinc-900 px-2 py-1">
                  <div className="text-zinc-500">Match document_date</div>
                  <div className="font-mono font-bold text-amber-300">{diag.imports_matching_document_date_query}</div>
                </div>
              </div>
              {(diag.recent_imports || []).length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-zinc-400">Ultimele importuri (click pentru detalii)</summary>
                  <div className="mt-1 max-h-48 space-y-1 overflow-y-auto">
                    {diag.recent_imports.map((r, i) => (
                      <div key={i} className="rounded bg-zinc-900 px-2 py-1 font-mono text-[10px] text-zinc-300">
                        doc_date=<b className="text-amber-300">{r.document_date || "—"}</b> · stock_date=<b className="text-emerald-300">{r.stock_date || "—"}</b> · {r.document_number || "—"} · {r.supplier_name || "—"} · {r.items_count} SKU
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {diag.aggregator_result_keys_count > 0 && (
                <div className="text-zinc-400">Aggregator returnează cantități pentru <b>{diag.aggregator_result_keys_count}</b> SKU-uri distincte.</div>
              )}
              {diag.imports_matching_stock_date_query === 0 && diag.imports_matching_document_date_query > 0 && (
                <div className="mt-1 rounded bg-amber-500/10 px-2 py-1 text-amber-300">
                  ⚠ Ai importuri cu <code>document_date</code> în această lună, dar <code>stock_date</code> e altă lună. Videnda se raportează după <b>Data Stoc</b> (când marfa intră fizic în stoc), nu după data facturii. Editează importurile pe pagina Importuri și corectează câmpul &quot;Data intrare stoc&quot;.
                </div>
              )}
              {diag.imports_matching_stock_date_query === 0 && diag.imports_matching_document_date_query === 0 && diag.imports_total_in_db > 0 && (
                <div className="mt-1 rounded bg-red-500/10 px-2 py-1 text-red-300">
                  ⚠ Ai <b>{diag.imports_total_in_db}</b> importuri în DB, dar niciunul cu <code>stock_date</code> sau <code>document_date</code> în luna aleasă. Deschide detaliile de mai sus ca să vezi datele reale ale importurilor.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            data-testid="videnda-templates-close"
            onClick={onClose}
            className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-orange-500/40"
          >
            Închide
          </button>
        </div>
      </div>
    </div>
  );
}

function VidendaResendMonthModal({ onClose }) {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [year, setYear] = useState(String(prev.getFullYear()));
  const [month, setMonth] = useState(String(prev.getMonth() + 1));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!window.confirm(`Retrimit raportul Videnda pentru ${month}/${year} folosind ȘABLONELE CURENTE?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/settings/background-loops/videnda_monthly_report/resend-month`, {
        year: Number(year), month: Number(month),
      });
      const res = r.data?.result || {};
      toast.success(`Trimis: ${res.month_label || ""} → ${(res.recipients || []).join(", ")}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        data-testid="videnda-resend-modal"
        className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 text-base font-semibold text-zinc-100">Retrimite raportul Videnda</h3>
        <p className="mb-4 text-xs text-zinc-500">
          Rebuildează cele 3 fișe pentru luna aleasă, folosind șabloanele curente, și trimite email către destinatarii configurați.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Anul</label>
            <input
              data-testid="videnda-resend-year"
              type="number" min="2020" max="2100"
              value={year} onChange={(e) => setYear(e.target.value)}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Luna</label>
            <select
              data-testid="videnda-resend-month"
              value={month} onChange={(e) => setMonth(e.target.value)}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 focus:border-orange-500/50 focus:outline-none"
            >
              {["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"].map((n, i) => (
                <option key={i} value={i+1}>{String(i+1).padStart(2,"0")} · {n}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-orange-500/40"
          >
            Anulează
          </button>
          <button
            data-testid="videnda-resend-submit"
            onClick={submit} disabled={busy}
            className="rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-200 hover:bg-violet-500/20 disabled:opacity-50"
          >
            {busy ? "Se trimite..." : "Retrimite"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ open, onClose, history, title }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        data-testid="loop-history-modal"
        className="w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-base font-semibold text-zinc-100">{title || "Istoric"}</h3>
        {(!history || history.length === 0) ? (
          <p className="text-sm text-zinc-500">Niciun email trimis încă.</p>
        ) : (
          <div className="max-h-96 space-y-1 overflow-y-auto">
            {history.map((h, i) => {
              const isRma = !!h.emag_id;
              const isVidendaRec = h.kind === "videnda_monthly" || h.month_label;
              const ok = h.ok ?? !h.error;
              let label;
              if (isRma) {
                label = `RMA #${h.emag_id} → ${(h.recipients || []).join(", ") || h.to || "—"} · prag ${h.days_threshold}z · deadline ${h.target_date || "—"}`;
              } else if (isVidendaRec) {
                label = `${h.month_label || "—"} → ${(h.recipients || []).join(", ") || "—"}`;
              } else {
                label = h.to || "—";
              }
              return (
                <div key={i} className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-xs ${
                  ok ? "border-emerald-500/20 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/10"
                }`}>
                  {ok ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-400" /> : <AlertCircle className="h-4 w-4 flex-shrink-0 text-rose-400" />}
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium text-zinc-100">{label}</p>
                    {h.error && <p className="truncate text-rose-300">{h.error}</p>}
                    {h.errors && h.errors.length > 0 && <p className="truncate text-rose-300">{h.errors.join("; ")}</p>}
                  </div>
                  <span className="text-zinc-500">{fmtRelative(h.sent_at)}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="rounded-lg bg-zinc-800 px-4 py-1.5 text-xs text-zinc-100 hover:bg-zinc-700">Închide</button>
        </div>
      </div>
    </div>
  );
}

export default function BackgroundLoopsSection() {
  const [loops, setLoops] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [historyTitle, setHistoryTitle] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        axios.get(`${API}/settings/background-loops`),
        axios.get(`${API}/email-templates`).catch(() => ({ data: { items: [] } })),
      ]);
      setLoops(r.data?.loops || []);
      setTemplates(t.data?.items || t.data?.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare încărcare procese");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (key, payload) => {
    await axios.put(`${API}/settings/background-loops/${key}`, payload);
    await load();
  };

  const testSend = async (key) => {
    if (key === "fbe_weekly_report") {
      await axios.post(`${API}/settings/background-loops/fbe_weekly_report/test-send`);
    } else if (key === "rma_incident_alert") {
      const r = await axios.post(`${API}/settings/background-loops/rma_incident_alert/test-send`);
      const res = r.data?.result || {};
      if (res.matched === 0) {
        toast.info(`Niciun retur BAU nu are exact ${res.days_threshold} zile până la incident azi (${res.target_date}).`);
      } else if (res.sent > 0) {
        toast.success(`${res.sent} email-uri trimise pentru ${res.fresh ?? res.matched} retururi (deadline ${res.target_date}).`);
      } else {
        toast.info(`${res.matched} retururi găsite — toate au fost deja notificate.`);
      }
    } else if (key === "videnda_monthly_report") {
      const r = await axios.post(`${API}/settings/background-loops/videnda_monthly_report/test-send`);
      const res = r.data?.result || {};
      if (res.errors && res.errors.length) {
        toast.error(`Eroare la trimitere: ${res.errors.join("; ")}`);
      } else {
        toast.success(`${res.sent} email trimis pentru luna ${res.month_label} (${(res.filenames || []).length} fișiere).`);
      }
    } else if (key === "monthly_returns_report" || key === "monthly_transport_report") {
      const r = await axios.post(`${API}/settings/background-loops/${key}/test-send`);
      const period = r.data?.period || "intervalul curent";
      toast.success(`Raport trimis pentru ${period}. Verifică inbox-ul (Excel atașat).`);
    }
  };

  const showHistory = async (key) => {
    try {
      if (key === "fbe_weekly_report") {
        const r = await axios.get(`${API}/settings/background-loops/fbe_weekly_report/history`);
        setHistoryTitle("Istoric trimiteri Raport FBE");
        setHistory(r.data?.history || []);
      } else if (key === "rma_incident_alert") {
        const r = await axios.get(`${API}/settings/background-loops/rma_incident_alert/history`);
        setHistoryTitle("Istoric alerte retururi BAU");
        setHistory(r.data?.history || []);
      } else if (key === "videnda_monthly_report") {
        const r = await axios.get(`${API}/settings/background-loops/videnda_monthly_report/history`);
        setHistoryTitle("Istoric raport lunar Videnda");
        setHistory(r.data?.history || []);
      } else if (key === "monthly_returns_report") {
        const r = await axios.get(`${API}/settings/background-loops/monthly_returns_report/history`);
        setHistoryTitle("Istoric raport bilunar Retururi/Storno");
        setHistory(r.data?.history || []);
      } else if (key === "monthly_transport_report") {
        const r = await axios.get(`${API}/settings/background-loops/monthly_transport_report/history`);
        setHistoryTitle("Istoric raport bilunar Transport Sameday");
        setHistory(r.data?.history || []);
      }
    } catch (e) {
      toast.error("Nu pot citi istoricul");
    }
  };

  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="background-loops-section">
      <div className="mb-4 flex items-center gap-2">
        <Power className="h-4 w-4 text-zinc-400" />
        <h2 className="text-base font-semibold text-zinc-100">Procese automate</h2>
      </div>
      <p className="mb-4 text-xs text-zinc-500">
        Toate task-urile care rulează în background (sincronizări, polling, rapoarte). Le poți activa/dezactiva și ajusta frecvența.
      </p>
      {loading ? (
        <p className="py-6 text-center text-sm text-zinc-500">Se încarcă...</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {loops.map((l) => (
            <LoopCard key={l.key} loop={l} templates={templates} onPatch={patch} onTestSend={testSend} onShowHistory={showHistory} />
          ))}
        </div>
      )}
      <HistoryModal open={!!history} onClose={() => setHistory(null)} history={history} title={historyTitle} />
    </section>
  );
}
